'use client'

import { Send } from 'lucide-react'
import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { CATEGORIES, COURSES, getCourse, type CategoryId, type Locale } from '@/data/catalog'
import {
  NCP_CONTACT_ENDPOINT,
  NCP_LOCAL_FORM_BYPASS,
  NCP_RECAPTCHA_ENABLED,
  NCP_RECAPTCHA_SITE_KEY,
} from '@/lib/ncp-env'

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

/** Course dropdown value for visitors who want us to recommend a course */
export const UNDECIDED_COURSE = 'not-sure'

const TEXT = {
  en: {
    name: 'Full name',
    namePh: 'Your full name',
    email: 'Email',
    phone: 'Phone / WhatsApp',
    city: 'City',
    cityPh: 'e.g. Lahore',
    category: 'What would you like to study?',
    course: 'Course',
    coursePh: 'Select a course',
    courseFirst: 'Choose an area above first',
    undecided: 'Not sure yet',
    undecidedNote: 'No problem. Our admissions team will recommend a course based on your goals.',
    pickCourse: 'Please choose what you would like to study.',
    qualification: 'Highest qualification',
    qualificationPh: 'Select your qualification',
    qualifications: ['Matric / O Level', 'Intermediate / A Level', 'Diploma / DAE', "Bachelor's degree", "Master's degree or higher", 'Other'],
    japanese: 'Current Japanese level',
    japaneseLevels: ['Complete beginner', 'N5', 'N4', 'N3', 'N2', 'N1'],
    mode: 'Preferred mode',
    modes: ['On campus', 'Online'],
    message: 'Anything else? (optional)',
    messagePh: 'Your goals, preferred timings or any questions',
    submit: 'Submit application',
    submitting: 'Submitting...',
    submitted: 'Application received',
    success: 'Thank you! Our admissions team will contact you within 24 hours.',
    error: 'There was an error submitting your application. Please try again.',
    endpoint: 'The application service is not configured. Please contact us by phone or WhatsApp.',
    network: 'Could not reach the application service. Please check your connection and try again.',
  },
  ja: {
    name: '氏名',
    namePh: 'フルネーム',
    email: 'メールアドレス',
    phone: '電話 / WhatsApp',
    city: '都市',
    cityPh: '例：ラホール',
    category: '学びたい分野',
    course: 'コース',
    coursePh: 'コースを選択',
    courseFirst: '先に分野を選んでください',
    undecided: 'まだ決めていない',
    undecidedNote: 'ご安心ください。目標に合わせて担当者がおすすめのコースをご提案します。',
    pickCourse: '学びたい分野を選んでください。',
    qualification: '最終学歴',
    qualificationPh: '学歴を選択',
    qualifications: ['Matric / O Level', 'Intermediate / A Level', 'ディプロマ / DAE', '学士', '修士以上', 'その他'],
    japanese: '現在の日本語レベル',
    japaneseLevels: ['まったくの初心者', 'N5', 'N4', 'N3', 'N2', 'N1'],
    mode: '希望の受講形式',
    modes: ['通学', 'オンライン'],
    message: 'その他（任意）',
    messagePh: '目標、希望の時間帯、ご質問など',
    submit: '申し込む',
    submitting: '送信中...',
    submitted: '受付完了',
    success: 'ありがとうございます。24時間以内に担当者よりご連絡いたします。',
    error: '送信中にエラーが発生しました。もう一度お試しください。',
    endpoint: '申込サービスが設定されていません。お電話またはWhatsAppでご連絡ください。',
    network: '申込サービスに接続できませんでした。通信環境をご確認のうえ、もう一度お試しください。',
  },
}

type CategoryChoice = CategoryId | typeof UNDECIDED_COURSE | ''

const categoryOf = (course: string): CategoryChoice =>
  course === UNDECIDED_COURSE ? UNDECIDED_COURSE : getCourse(course)?.category ?? ''

const emptyForm = (course = '') => ({
  name: '',
  email: '',
  phone: '',
  city: '',
  category: categoryOf(course),
  course,
  qualification: '',
  japaneseLevel: '',
  mode: '',
  message: '',
})

interface ApplyFormProps {
  locale: Locale
  /** Course slug to preselect, e.g. from an "Apply for this course" button */
  initialCourse?: string
}

/**
 * Course application form, used by the Apply modal and the /register page.
 * Submits to the same endpoint as the contact form (format: 'application').
 */
export default function ApplyForm({ locale, initialCourse = '' }: ApplyFormProps) {
  const t = TEXT[locale]
  const [formData, setFormData] = useState(() => emptyForm(initialCourse))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(
    !NCP_RECAPTCHA_ENABLED || NCP_LOCAL_FORM_BYPASS
  )

  useEffect(() => {
    setFormData((prev) => ({ ...prev, category: categoryOf(initialCourse), course: initialCourse }))
  }, [initialCourse])

  useEffect(() => {
    if (recaptchaLoaded) return
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => setRecaptchaLoaded(true))
      return
    }
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${NCP_RECAPTCHA_SITE_KEY}`
    script.async = true
    script.defer = true
    script.onload = () => window.grecaptcha?.ready(() => setRecaptchaLoaded(true))
    document.head.appendChild(script)
  }, [recaptchaLoaded])

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const selectCategory = (category: CategoryChoice) => {
    setError(null)
    setFormData((prev) =>
      prev.category === category
        ? prev
        : { ...prev, category, course: category === UNDECIDED_COURSE ? UNDECIDED_COURSE : '' }
    )
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    // The category buttons can't use native `required`, so check them here
    if (!formData.category) {
      setError(t.pickCourse)
      return
    }
    setError(null)
    setIsSubmitting(true)

    try {
      if (NCP_LOCAL_FORM_BYPASS) {
        await new Promise((resolve) => setTimeout(resolve, 400))
        setSubmitted(true)
        setFormData(emptyForm(initialCourse))
        return
      }

      if (!NCP_CONTACT_ENDPOINT) {
        throw new Error('Contact endpoint is not configured')
      }

      let recaptchaToken = ''
      if (NCP_RECAPTCHA_ENABLED) {
        if (!recaptchaLoaded || !window.grecaptcha) {
          throw new Error('reCAPTCHA is still loading. Please wait a moment and try again.')
        }
        try {
          await new Promise<void>((resolve) => window.grecaptcha.ready(resolve))
          recaptchaToken = await window.grecaptcha.execute(NCP_RECAPTCHA_SITE_KEY, {
            action: 'submit_ncp_apply_form',
          })
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError)
          throw new Error('reCAPTCHA verification failed. Please refresh the page and try again.')
        }
        if (!recaptchaToken) {
          throw new Error('reCAPTCHA verification failed. Please refresh the page and try again.')
        }
      }

      // Always send course names in English so the admissions inbox is consistent
      const courseName =
        formData.course === UNDECIDED_COURSE
          ? 'Not sure yet'
          : getCourse(formData.course)?.title.en || formData.course

      // The contact service only stores name/email/phone/level/format/message,
      // so the application details are also written into the message body.
      const details = [
        `Course: ${courseName}`,
        `Qualification: ${formData.qualification}`,
        `Japanese level: ${formData.japaneseLevel || '-'}`,
        `Preferred mode: ${formData.mode || '-'}`,
        `City: ${formData.city || '-'}`,
        formData.message && `\n${formData.message}`,
      ]
        .filter(Boolean)
        .join('\n')

      const response = await fetch(NCP_CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          fullName: formData.name,
          email: formData.email,
          phone: formData.phone,
          level: `Application: ${courseName}`,
          format: 'application',
          message: details,
          course: courseName,
          // Course name in the visitor's language, for the thank-you email
          courseLocalized: getCourse(formData.course)?.title[locale] || courseName,
          qualification: formData.qualification,
          japaneseLevel: formData.japaneseLevel,
          mode: formData.mode,
          city: formData.city,
          note: formData.message,
          locale,
          recaptchaToken,
        }),
      })

      const json = await response.json().catch(() => null)
      if (!response.ok) {
        throw new Error(json?.error || 'Failed to submit application')
      }
      if (json && json.ok === false) {
        throw new Error(json.error || 'Failed to submit application')
      }

      setSubmitted(true)
      setFormData(emptyForm(initialCourse))
    } catch (submitError) {
      console.error('NCP apply form error:', submitError)
      const message = submitError instanceof Error ? submitError.message : ''
      setError(
        message === 'Contact endpoint is not configured'
          ? t.endpoint
          : message === 'Failed to fetch'
            ? t.network
            : message || t.error
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="py-10 text-center space-y-3">
        <p className="type-card-title text-brand-green">{t.submitted}</p>
        <p className="type-body text-muted">{t.success}</p>
      </div>
    )
  }

  const submitDisabled =
    isSubmitting || (NCP_RECAPTCHA_ENABLED && !NCP_LOCAL_FORM_BYPASS && !recaptchaLoaded)

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="apply-name" className="ncp-field-label">
          {t.name} <span className="ncp-field-required">*</span>
        </label>
        <input
          type="text"
          id="apply-name"
          name="name"
          required
          value={formData.name}
          onChange={handleChange}
          className="ncp-field-input focus-ring"
          placeholder={t.namePh}
          autoComplete="name"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="apply-email" className="ncp-field-label">
            {t.email} <span className="ncp-field-required">*</span>
          </label>
          <input
            type="email"
            id="apply-email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="ncp-field-input focus-ring"
            placeholder="your.email@example.com"
            autoComplete="email"
          />
        </div>
        <div>
          <label htmlFor="apply-phone" className="ncp-field-label">
            {t.phone} <span className="ncp-field-required">*</span>
          </label>
          <input
            type="tel"
            id="apply-phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            className="ncp-field-input focus-ring"
            placeholder="+92 300 1234567"
            autoComplete="tel"
          />
        </div>
      </div>

      <fieldset>
        <legend className="ncp-field-label">
          {t.category} <span className="ncp-field-required">*</span>
        </legend>
        <div role="radiogroup" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {[
            ...CATEGORIES.map((cat) => ({ id: cat.id as CategoryChoice, label: cat.title[locale] })),
            { id: UNDECIDED_COURSE as CategoryChoice, label: t.undecided },
          ].map(({ id, label }) => {
            const selected = formData.category === id
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => selectCategory(id)}
                className={`rounded-xl border px-3 py-2.5 text-left text-sm font-medium leading-snug transition-colors focus-ring ${
                  selected
                    ? 'border-brand-green bg-brand-green text-white shadow-sm'
                    : 'border-brand-cool-gray bg-white text-brand-charcoal hover:border-brand-green/50 hover:bg-brand-green-soft'
                }`}
              >
                {label}
              </button>
            )
          })}
        </div>
      </fieldset>

      {formData.category === UNDECIDED_COURSE ? (
        <p className="rounded-xl bg-brand-soft-gray px-4 py-3 text-sm text-brand-charcoal-75">{t.undecidedNote}</p>
      ) : (
        <div>
          <label htmlFor="apply-course" className="ncp-field-label">
            {t.course} <span className="ncp-field-required">*</span>
          </label>
          <select
            id="apply-course"
            name="course"
            required
            disabled={!formData.category}
            value={formData.course}
            onChange={handleChange}
            className="ncp-field-input focus-ring"
          >
            <option value="">{formData.category ? t.coursePh : t.courseFirst}</option>
            {COURSES.filter((c) => c.category === formData.category).map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.title[locale]}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="apply-qualification" className="ncp-field-label">
            {t.qualification} <span className="ncp-field-required">*</span>
          </label>
          <select
            id="apply-qualification"
            name="qualification"
            required
            value={formData.qualification}
            onChange={handleChange}
            className="ncp-field-input focus-ring"
          >
            <option value="">{t.qualificationPh}</option>
            {t.qualifications.map((q, i) => (
              <option key={q} value={TEXT.en.qualifications[i]}>
                {q}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="apply-japanese" className="ncp-field-label">
            {t.japanese}
          </label>
          <select
            id="apply-japanese"
            name="japaneseLevel"
            value={formData.japaneseLevel}
            onChange={handleChange}
            className="ncp-field-input focus-ring"
          >
            <option value="">-</option>
            {t.japaneseLevels.map((l, i) => (
              <option key={l} value={TEXT.en.japaneseLevels[i]}>
                {l}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="apply-mode" className="ncp-field-label">
            {t.mode}
          </label>
          <select
            id="apply-mode"
            name="mode"
            value={formData.mode}
            onChange={handleChange}
            className="ncp-field-input focus-ring"
          >
            <option value="">-</option>
            {t.modes.map((m, i) => (
              <option key={m} value={TEXT.en.modes[i]}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="apply-city" className="ncp-field-label">
            {t.city}
          </label>
          <input
            type="text"
            id="apply-city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            className="ncp-field-input focus-ring"
            placeholder={t.cityPh}
            autoComplete="address-level2"
          />
        </div>
      </div>

      <div>
        <label htmlFor="apply-message" className="ncp-field-label">
          {t.message}
        </label>
        <textarea
          id="apply-message"
          name="message"
          rows={3}
          value={formData.message}
          onChange={handleChange}
          className="ncp-field-input focus-ring resize-none"
          placeholder={t.messagePh}
        />
      </div>

      <button
        type="submit"
        disabled={submitDisabled}
        className={`btn-primary w-full gap-2 ${submitDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isSubmitting ? t.submitting : t.submit}
        {!isSubmitting && <Send className="w-4 h-4" />}
      </button>

      {error && <p className="text-sm text-brand-red text-center">{error}</p>}
    </form>
  )
}
